import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CreditCard, Loader2, CheckCircle2, ShieldCheck, XCircle } from "lucide-react";

export default function RegistroTarjetaPublicaPage() {
  const { token } = useParams();
  const [estado, setEstado] = useState("cargando"); // cargando | valido | invalido | guardado
  const [cliente, setCliente] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [form, setForm] = useState({ titular: "", numero: "", vencimiento: "", cvv: "" });
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cobros/publico/${token}`)
      .then((r) => { setCliente(r.data.cliente); setLogoUrl(r.data.logo_url || ""); setEstado("valido"); })
      .catch(() => setEstado("invalido"));
  }, [token]);

  const guardar = async () => {
    setError("");
    try {
      setGuardando(true);
      await axios.post(`${API}/cobros/publico/${token}`, form);
      setEstado("guardado");
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar. Verifique los datos.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0C314C] flex items-center justify-center p-4" data-testid="registro-tarjeta-publica">
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <div className="text-center mb-5">
            {logoUrl ? (
              <img src={logoUrl} alt="QAsesores" className="h-14 mx-auto mb-3 object-contain" data-testid="logo-empresa"
                onError={(e) => {
                  if (!e.target.dataset.fb) { e.target.dataset.fb = "1"; e.target.src = "/logo-qasesores.png"; }
                  else setLogoUrl("");
                }} />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#0C314C] flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
            )}
            <h1 className="text-xl font-bold">Registro Seguro de Tarjeta</h1>
            <p className="text-sm text-slate-500">QAsesores Corredores de Seguros</p>
          </div>

          {estado === "cargando" && <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>}

          {estado === "invalido" && (
            <div className="text-center py-6" data-testid="link-invalido">
              <XCircle className="w-12 h-12 mx-auto mb-2 text-red-400" />
              <p className="font-medium">Este link no es válido o ya fue utilizado</p>
              <p className="text-sm text-slate-500 mt-1">Contacte a su ejecutivo para recibir uno nuevo.</p>
            </div>
          )}

          {estado === "guardado" && (
            <div className="text-center py-6" data-testid="registro-exitoso">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
              <p className="font-medium">¡Tarjeta registrada con éxito!</p>
              <p className="text-sm text-slate-500 mt-1">Su ejecutivo ya fue notificado. Puede cerrar esta página.</p>
            </div>
          )}

          {estado === "valido" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-300">Hola <strong>{cliente}</strong>, complete los datos de su tarjeta. La información se guarda cifrada y protegida.</p>
              <div><Label>Nombre del titular</Label><Input value={form.titular} onChange={(e) => setForm({ ...form, titular: e.target.value })} data-testid="pub-titular" /></div>
              <div><Label>Número de tarjeta</Label><Input inputMode="numeric" placeholder="0000 0000 0000 0000" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="pub-numero" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vencimiento (MM/AA)</Label><Input placeholder="09/28" value={form.vencimiento} onChange={(e) => setForm({ ...form, vencimiento: e.target.value })} data-testid="pub-vencimiento" /></div>
                <div><Label>CVV</Label><Input inputMode="numeric" maxLength={4} value={form.cvv} onChange={(e) => setForm({ ...form, cvv: e.target.value })} data-testid="pub-cvv" /></div>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="pub-error">{error}</p>}
              <Button onClick={guardar} disabled={guardando || !form.titular || !form.numero || !form.vencimiento || !form.cvv}
                className="w-full bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="pub-guardar-btn">
                {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Guardar de forma segura
              </Button>
              <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1"><ShieldCheck className="w-3 h-3" />Datos cifrados — solo personal autorizado con aprobación de supervisor puede verlos</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
